import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArchiveIcon from '@mui/icons-material/Archive';
import CasinoIcon from '@mui/icons-material/Casino';
import GroupsIcon from '@mui/icons-material/Groups';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import EventIcon from '@mui/icons-material/Event';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import BoltIcon from '@mui/icons-material/Bolt';
import { useSnackbar } from 'notistack';
import { PageHeader, ConfirmDialog, ActionMenu, ResponsiveModal, ActionMenuItem } from '../components/common';
import { toPersianDigits } from '../components/common/JalaliDatePicker';
import { formatDate } from '../utils/dateUtils';
import raffleService, {
  RaffleItem,
  RaffleInput,
  RafflePoolPreview,
  RaffleStatus,
  RaffleRuleType,
  RaffleWinner,
  RAFFLE_STATUS_LABELS,
  RAFFLE_RULE_LABELS,
} from '../services/raffle.service';
import RaffleFormModal from '../components/raffle/RaffleFormModal';
import RaffleDrawStage from '../components/raffle/RaffleDrawStage';
import '../assets/fonts/lalezar/lalezar.css';

const GOLD = '#F5B301';
/** فونت نمایشی هویت ماژول قرعه‌کشی */
const DISPLAY_FONT = 'Lalezar, Vazirmatn, sans-serif';

const STATUS_META: Record<RaffleStatus, { color: string; label: string }> = {
  draft: { color: '#3B82F6', label: RAFFLE_STATUS_LABELS.draft },
  drawn: { color: '#22C55E', label: RAFFLE_STATUS_LABELS.drawn },
  archived: { color: '#94A3B8', label: RAFFLE_STATUS_LABELS.archived },
};

const RULE_ICONS: Record<RaffleRuleType, React.ReactNode> = {
  app_installed: <PhoneIphoneIcon sx={{ fontSize: 14 }} />,
  signup_range: <EventIcon sx={{ fontSize: 14 }} />,
  min_purchases: <ShoppingBagIcon sx={{ fontSize: 14 }} />,
  recent_activity: <BoltIcon sx={{ fontSize: 14 }} />,
};

const formatAmount = (amount?: number): string =>
  amount !== undefined && amount !== null ? `${Number(amount).toLocaleString('fa-IR')} تومان` : '—';

const medalColor = (rank: number): string | null =>
  rank === 1 ? GOLD : rank === 2 ? '#9AA5B1' : rank === 3 ? '#C77B30' : null;

const RaffleManagement: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const dark = theme.palette.mode === 'dark';

  const [items, setItems] = useState<RaffleItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [status, setStatus] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<RaffleItem | null>(null);

  const [detail, setDetail] = useState<RaffleItem | null>(null);
  const [poolPreview, setPoolPreview] = useState<RafflePoolPreview | null>(null);
  const [poolLoading, setPoolLoading] = useState(false);

  const [pendingDraw, setPendingDraw] = useState<RaffleItem | null>(null);
  const [pendingDelete, setPendingDelete] = useState<RaffleItem | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [stageRaffle, setStageRaffle] = useState<RaffleItem | null>(null);
  const [stageSample, setStageSample] = useState<string[]>([]);
  const [stageLoadingId, setStageLoadingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await raffleService.list({
        page,
        limit: 12,
        search: search.trim() || undefined,
        status: status || undefined,
      });
      setItems(res.items);
      setPages(res.pages || 1);
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'خطا در دریافت لیست قرعه‌کشی‌ها', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, search, status, enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  // ---------------------------------------------------------------- actions

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (row: RaffleItem) => {
    setEditing(row);
    setFormOpen(true);
  };

  const handleFormSubmit = async (input: RaffleInput) => {
    if (editing) {
      await raffleService.update(editing._id, input);
      enqueueSnackbar('قرعه‌کشی به‌روزرسانی شد', { variant: 'success' });
    } else {
      await raffleService.create(input);
      enqueueSnackbar('قرعه‌کشی ساخته شد', { variant: 'success' });
    }
    setFormOpen(false);
    setEditing(null);
    await load();
  };

  const openDetail = async (row: RaffleItem) => {
    setDetail(row);
    setPoolPreview(null);
    setPoolLoading(true);
    try {
      const [fresh, preview] = await Promise.all([raffleService.get(row._id), raffleService.poolPreview(row._id)]);
      setDetail(fresh);
      setPoolPreview(preview);
    } catch {
      // پیش‌نمایش استخر ضروری نیست
    } finally {
      setPoolLoading(false);
    }
  };

  const runDraw = async () => {
    if (!pendingDraw) return;
    setActionLoading(true);
    try {
      const result = await raffleService.draw(pendingDraw._id);
      setPendingDraw(null);
      setStageRaffle(result.raffle);
      setStageSample(result.candidatesSample || []);
      await load();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || e?.message || 'خطا در اجرای قرعه‌کشی', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const openShow = async (row: RaffleItem) => {
    setStageLoadingId(row._id);
    try {
      const [fresh, sample] = await Promise.all([raffleService.get(row._id), raffleService.candidatesSample(row._id)]);
      setStageRaffle(fresh);
      setStageSample(sample);
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'خطا در بارگذاری نمایش', { variant: 'error' });
    } finally {
      setStageLoadingId(null);
    }
  };

  /** رد برندهٔ فعلی رتبه و انتخاب جایگزین — صحنه خودش انیمیشن را اجرا می‌کند */
  const handleRedrawRank = async (rank: number): Promise<RaffleWinner> => {
    if (!stageRaffle) throw new Error('no raffle');
    const res = await raffleService.redraw(stageRaffle._id, rank);
    setStageRaffle(res.raffle);
    load();
    return res.newWinner;
  };

  const runDelete = async () => {
    if (!pendingDelete) return;
    setActionLoading(true);
    try {
      await raffleService.remove(pendingDelete._id);
      enqueueSnackbar('قرعه‌کشی حذف شد', { variant: 'success' });
      setPendingDelete(null);
      await load();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || e?.message || 'خطا در حذف', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const archiveRow = async (row: RaffleItem) => {
    try {
      await raffleService.archive(row._id);
      enqueueSnackbar('قرعه‌کشی آرشیو شد', { variant: 'success' });
      await load();
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'خطا در آرشیو', { variant: 'error' });
    }
  };

  const rowMenuItems = (row: RaffleItem): ActionMenuItem[] => {
    const menu: ActionMenuItem[] = [{ label: 'جزئیات', icon: <InfoOutlinedIcon fontSize="small" />, onClick: () => openDetail(row) }];
    if (row.status === 'draft') {
      menu.push({ label: 'ویرایش', icon: <EditIcon fontSize="small" />, onClick: () => openEdit(row) });
      menu.push({ label: 'حذف', icon: <DeleteIcon fontSize="small" />, color: 'error', onClick: () => setPendingDelete(row) });
    }
    if (row.status === 'drawn') {
      menu.push({ label: 'آرشیو', icon: <ArchiveIcon fontSize="small" />, onClick: () => archiveRow(row) });
    }
    return menu;
  };

  // ------------------------------------------------------------- raffle card

  const renderCard = (row: RaffleItem) => {
    const meta = STATUS_META[row.status];
    const enabledRules = (row.rules || []).filter((r) => r.enabled);
    const prizes = [...(row.prizes || [])].sort((a, b) => a.rank - b.rank);
    const firstPrize = prizes[0];

    return (
      <Grid item xs={12} sm={6} lg={4} key={row._id}>
        <Box
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            transition: 'transform .2s, box-shadow .2s',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            '&:hover': {
              transform: 'translateY(-3px)',
              boxShadow: `0 14px 40px ${alpha(meta.color, 0.18)}`,
            },
          }}
        >
          {/* هدر تیرهٔ کارت — امضای بصری ماژول */}
          <Box
            sx={{
              px: 2.5,
              pt: 2,
              pb: 1.75,
              color: '#fff',
              position: 'relative',
              background: `
                radial-gradient(220px 120px at 85% -20%, ${alpha(meta.color, 0.45)} 0%, transparent 70%),
                linear-gradient(150deg, #141B33 0%, #0B1020 100%)
              `,
            }}
          >
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 19, lineHeight: 1.4, fontFamily: DISPLAY_FONT }} noWrap title={row.title}>
                  {row.title}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                  {row.status === 'draft'
                    ? `ساخته‌شده ${formatDate(row.createdAt)}`
                    : `قرعه‌کشی ${row.draw?.drawnAt ? formatDate(row.draw.drawnAt) : '—'}`}
                </Typography>
              </Box>
              <Chip
                size="small"
                label={meta.label}
                sx={{
                  bgcolor: alpha(meta.color, 0.2),
                  color: meta.color,
                  fontWeight: 800,
                  border: `1px solid ${alpha(meta.color, 0.45)}`,
                  flexShrink: 0,
                }}
              />
            </Stack>
          </Box>

          <Stack spacing={1.5} sx={{ p: 2.5, flex: 1 }}>
            {/* قوانین ضریب */}
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {enabledRules.length ? (
                enabledRules.map((r) => (
                  <Chip
                    key={r.type}
                    size="small"
                    icon={<>{RULE_ICONS[r.type]}</>}
                    label={`${RAFFLE_RULE_LABELS[r.type]} ×${toPersianDigits(String(r.multiplier))}`}
                    variant="outlined"
                    sx={{ fontSize: 11.5, '& .MuiChip-icon': { color: GOLD } }}
                  />
                ))
              ) : (
                <Typography variant="caption" color="text.secondary">
                  بدون قانون ضریب — شانس همه برابر
                </Typography>
              )}
            </Stack>

            {/* جایزه و آمار */}
            <Stack direction="row" spacing={1}>
              <Box
                sx={{
                  flex: 1,
                  borderRadius: 2.5,
                  p: 1.25,
                  bgcolor: alpha(GOLD, dark ? 0.09 : 0.07),
                  border: `1px solid ${alpha(GOLD, 0.25)}`,
                }}
              >
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <CardGiftcardIcon sx={{ fontSize: 16, color: GOLD }} />
                  <Typography variant="caption" fontWeight={700}>
                    {toPersianDigits(String(prizes.length))} جایزه
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                  {firstPrize ? `🥇 ${firstPrize.title}` : '—'}
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  borderRadius: 2.5,
                  p: 1.25,
                  bgcolor: alpha('#B25373', dark ? 0.1 : 0.06),
                  border: `1px solid ${alpha('#B25373', 0.22)}`,
                }}
              >
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <GroupsIcon sx={{ fontSize: 16, color: '#B25373' }} />
                  <Typography variant="caption" fontWeight={700}>
                    {row.status === 'draft' ? 'استخر باز' : `${toPersianDigits(String(row.draw?.participants ?? 0))} نفر`}
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {row.status === 'draft'
                    ? `${toPersianDigits(String(row.winnersCount))} برنده انتخاب می‌شود`
                    : `${toPersianDigits(String(row.winnersCount))} برنده${(row.redraws?.length || 0) ? ` · ${toPersianDigits(String(row.redraws!.length))} قرعهٔ مجدد` : ''}`}
                </Typography>
              </Box>
            </Stack>

            <Box sx={{ flex: 1 }} />

            {/* اکشن‌ها */}
            <Stack direction="row" spacing={1} alignItems="center">
              {row.status === 'draft' ? (
                <Button
                  fullWidth
                  startIcon={<CasinoIcon />}
                  onClick={() => setPendingDraw(row)}
                  sx={{
                    borderRadius: 2.5,
                    fontWeight: 800,
                    py: 0.9,
                    background: `linear-gradient(135deg, ${GOLD}, #FF8F00)`,
                    color: '#1A1200',
                    '&:hover': { background: 'linear-gradient(135deg, #FFC233, #FF9D1C)' },
                  }}
                >
                  اجرای قرعه‌کشی
                </Button>
              ) : (
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={
                    stageLoadingId === row._id ? <CircularProgress size={16} color="inherit" /> : <PlayCircleOutlineIcon />
                  }
                  disabled={stageLoadingId === row._id}
                  onClick={() => openShow(row)}
                  sx={{ borderRadius: 2.5, fontWeight: 700, py: 0.85 }}
                >
                  نمایش برنده‌ها
                </Button>
              )}
              <ActionMenu items={rowMenuItems(row)} />
            </Stack>
          </Stack>
        </Box>
      </Grid>
    );
  };

  // ------------------------------------------------------------------ render

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <PageHeader
        title="قرعه‌کشی کاربران"
        subtitle="قرعه‌کشی وزنی بین کاربران — شرط‌های ضریب شانس، انتخاب اثبات‌پذیر و نمایش زندهٔ برنده‌ها"
        breadcrumbs={[{ label: 'قرعه‌کشی کاربران' }]}
        actions={[
          { label: 'بروزرسانی', onClick: () => load(), icon: <RefreshIcon />, variant: 'outlined', color: 'primary' },
          { label: 'قرعه‌کشی جدید', onClick: openCreate, icon: <AddIcon /> },
        ]}
      />

      {/* نوار جستجو و فیلتر */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2.5 }}>
        <TextField
          size="small"
          placeholder="جستجو در عنوان قرعه‌کشی…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setSearch(searchInput);
              setPage(1);
            }
          }}
          sx={{ flex: 1, maxWidth: { sm: 420 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label="وضعیت"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          sx={{ width: { xs: '100%', sm: 180 } }}
        >
          <MenuItem value="">همه وضعیت‌ها</MenuItem>
          {(Object.keys(RAFFLE_STATUS_LABELS) as RaffleStatus[]).map((s) => (
            <MenuItem key={s} value={s}>
              {RAFFLE_STATUS_LABELS[s]}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {/* گرید کارت‌ها */}
      {loading && !items.length ? (
        <Grid container spacing={2.5}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} lg={4} key={i}>
              <Skeleton variant="rounded" height={260} sx={{ borderRadius: 4 }} />
            </Grid>
          ))}
        </Grid>
      ) : items.length ? (
        <>
          <Grid container spacing={2.5}>
            {items.map(renderCard)}
          </Grid>
          {pages > 1 && (
            <Stack direction="row" justifyContent="center" spacing={1} sx={{ mt: 3 }}>
              <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                قبلی
              </Button>
              <Typography variant="body2" sx={{ alignSelf: 'center' }}>
                {`${toPersianDigits(String(page))} از ${toPersianDigits(String(pages))}`}
              </Typography>
              <Button size="small" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
                بعدی
              </Button>
            </Stack>
          )}
        </>
      ) : (
        <Box
          sx={{
            borderRadius: 4,
            border: '1.5px dashed',
            borderColor: 'divider',
            py: 8,
            textAlign: 'center',
          }}
        >
          <CasinoIcon sx={{ fontSize: 64, color: alpha(GOLD, 0.5), mb: 1.5 }} />
          <Typography sx={{ fontFamily: DISPLAY_FONT, fontSize: 24, mb: 0.5 }}>
            هنوز قرعه‌کشی‌ای نساخته‌اید
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            با «قرعه‌کشی جدید» اولین قرعه‌کشی وزنی را بسازید
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ borderRadius: 2.5 }}>
            قرعه‌کشی جدید
          </Button>
        </Box>
      )}

      {/* فرم ساخت/ویرایش */}
      <RaffleFormModal
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onSubmit={handleFormSubmit}
        initial={editing}
      />

      {/* جزئیات */}
      <ResponsiveModal
        open={!!detail}
        onClose={() => setDetail(null)}
        title={detail?.title || ''}
        subtitle={detail ? RAFFLE_STATUS_LABELS[detail.status] : ''}
        icon={<CasinoIcon sx={{ color: GOLD }} />}
        maxWidth="md"
        mobileBreakpoint="md"
        loading={poolLoading && !poolPreview}
      >
        {detail && (
          <Stack spacing={2.5} sx={{ py: 1 }}>
            {detail.description && (
              <Typography variant="body2" color="text.secondary">
                {detail.description}
              </Typography>
            )}

            {poolPreview && (
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, p: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <GroupsIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle2">استخر شرکت‌کننده‌ها (لحظه‌ای)</Typography>
                </Stack>
                <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                  <Typography variant="body2">
                    کل: <b>{toPersianDigits(String(poolPreview.participants))}</b> نفر
                  </Typography>
                  <Typography variant="body2">
                    با ضریب: <b>{toPersianDigits(String(poolPreview.boostedCount))}</b> نفر
                  </Typography>
                  <Typography variant="body2">
                    مجموع وزن: <b>{toPersianDigits(String(poolPreview.totalWeight))}</b>
                  </Typography>
                </Stack>
                {poolPreview.ruleBreakdown.length > 0 && (
                  <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                    {poolPreview.ruleBreakdown.map((r) => (
                      <Chip
                        key={r.type}
                        size="small"
                        variant="outlined"
                        label={`${RAFFLE_RULE_LABELS[r.type]} ×${toPersianDigits(String(r.multiplier))} — ${toPersianDigits(String(r.matched))} نفر`}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            )}

            {/* جوایز (پیش از قرعه‌کشی) */}
            {detail.status === 'draft' && (
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <CardGiftcardIcon fontSize="small" sx={{ color: GOLD }} />
                  <Typography variant="subtitle2">جوایز ({toPersianDigits(String((detail.prizes || []).length))} برنده)</Typography>
                </Stack>
                <Stack spacing={0.75}>
                  {[...(detail.prizes || [])]
                    .sort((a, b) => a.rank - b.rank)
                    .map((p) => {
                      const mc = medalColor(p.rank);
                      return (
                        <Stack key={p.rank} direction="row" spacing={1.5} alignItems="center" sx={{ p: 0.75 }}>
                          <Chip
                            size="small"
                            label={toPersianDigits(String(p.rank))}
                            sx={{
                              fontWeight: 800,
                              minWidth: 40,
                              ...(mc ? { bgcolor: mc, color: '#fff' } : {}),
                            }}
                          />
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {p.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatAmount(p.amount)}
                          </Typography>
                        </Stack>
                      );
                    })}
                </Stack>
              </Box>
            )}

            {/* برنده‌ها */}
            {detail.status !== 'draft' && (detail.winners || []).length > 0 && (
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <EmojiEventsIcon fontSize="small" sx={{ color: GOLD }} />
                  <Typography variant="subtitle2">برنده‌ها</Typography>
                </Stack>
                <Stack spacing={0.75}>
                  {[...detail.winners]
                    .sort((a, b) => a.rank - b.rank)
                    .map((w) => {
                      const mc = medalColor(w.rank);
                      return (
                        <Stack
                          key={w.rank}
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                          sx={{ p: 1, borderRadius: 2, bgcolor: w.rank <= 3 ? 'action.hover' : 'transparent' }}
                        >
                          <Chip
                            size="small"
                            label={toPersianDigits(String(w.rank))}
                            sx={{ fontWeight: 800, minWidth: 40, ...(mc ? { bgcolor: mc, color: '#fff' } : {}) }}
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600}>
                              {w.name}
                            </Typography>
                            {w.phoneNumber && (
                              <Typography variant="caption" color="text.secondary" sx={{ direction: 'ltr', display: 'inline-block' }}>
                                {toPersianDigits(w.phoneNumber)}
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ textAlign: 'left' }}>
                            <Typography variant="body2">{w.prizeTitle}</Typography>
                            {w.prizeAmount !== undefined && w.prizeAmount !== null && (
                              <Typography variant="caption" color="text.secondary">
                                {formatAmount(w.prizeAmount)}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      );
                    })}
                </Stack>

                {/* تاریخچهٔ قرعه‌کشی مجدد */}
                {(detail.redraws || []).length > 0 && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <ReplayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>
                        تاریخچهٔ قرعه‌کشی مجدد
                      </Typography>
                    </Stack>
                    <Stack spacing={0.5}>
                      {detail.redraws!.map((r, i) => (
                        <Typography key={i} variant="caption" color="text.secondary">
                          {`رتبهٔ ${toPersianDigits(String(r.rank))}: «${r.removedName}» حذف شد → «${r.newName}» (${formatDate(r.at)})`}
                        </Typography>
                      ))}
                    </Stack>
                  </>
                )}

                {detail.draw?.seedHash && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all', display: 'block' }}>
                      اثبات سلامت — الگوریتم: {detail.draw.algorithm} · هش seed: {detail.draw.seedHash}
                    </Typography>
                  </>
                )}
              </Box>
            )}
          </Stack>
        )}
      </ResponsiveModal>

      {/* تایید اجرای قرعه‌کشی */}
      <ConfirmDialog
        open={!!pendingDraw}
        title="اجرای قرعه‌کشی"
        message={
          pendingDraw
            ? `قرعه‌کشی «${pendingDraw.title}» با ${toPersianDigits(String(pendingDraw.winnersCount))} برنده (برابر تعداد جوایز) اجرا شود؟ برنده‌ها همان لحظه قطعی می‌شوند.`
            : ''
        }
        confirmText="اجرا و شروع نمایش"
        confirmColor="primary"
        loading={actionLoading}
        onCancel={() => setPendingDraw(null)}
        onConfirm={runDraw}
      />

      {/* تایید حذف */}
      <ConfirmDialog
        open={!!pendingDelete}
        title="حذف قرعه‌کشی"
        message={pendingDelete ? `قرعه‌کشی «${pendingDelete.title}» حذف شود؟` : ''}
        confirmText="حذف"
        confirmColor="error"
        loading={actionLoading}
        onCancel={() => setPendingDelete(null)}
        onConfirm={runDelete}
      />

      {/* صحنهٔ نمایش قرعه‌کشی */}
      {stageRaffle && (
        <RaffleDrawStage
          open={!!stageRaffle}
          onClose={() => setStageRaffle(null)}
          raffle={stageRaffle}
          candidatesSample={stageSample}
          onRedrawRank={handleRedrawRank}
          onError={(msg) => enqueueSnackbar(msg, { variant: 'error' })}
        />
      )}
    </Box>
  );
};

export default RaffleManagement;
